import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogTrigger } from '@civilgrid/ui/components';
import { useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useSnapshot } from 'valtio';
import { addAgencyLink as apiAddLink } from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { AgencyLinkType } from '~/common/types';
import ControlledCheckbox from '~/components2/forms/controlled/ControlledCheckbox';
import ControlledInput from '~/components2/forms/controlled/ControlledInput';
import ControlledSelect from '~/components2/forms/controlled/ControlledSelect';
import ControlledTextArea from '~/components2/forms/controlled/ControlledTextArea';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';
import store from '../../../store';
import { buildSubRegionItems, SUB_REGION_NONE, subRegionFkValue } from '../../../subRegionOptions';
import { items, type TFormValues } from './common';

export default function AddLink() {
  const { agency, subGeometries } = useSnapshot(store) as typeof store;

  const form = useForm<TFormValues>({
    defaultValues: {
      title: '',
      description: '',
      url: '',
      type: AgencyLinkType.Other,
      isPrivate: false,
      agencySubGeometryId: SUB_REGION_NONE,
    },
  });

  const subRegionItems = useMemo(
    () =>
      buildSubRegionItems(subGeometries).map(({ value, label }) => ({
        value,
        label: <div className="text-xs">{label}</div>,
      })),
    [subGeometries],
  );

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    try {
      const agencySubGeometryId = subRegionFkValue(data.agencySubGeometryId);
      const res = await apiAddLink(agency!.id, {
        ...data,
        agencySubGeometryId,
      });
      if (!res.id) throw new Error('Bad Response!');

      Notify.info({ message: 'Agency link added' });

      if (agencySubGeometryId) {
        capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryMemberAssigned, {
          agency_id: agency!.id,
          sub_geometry_id: agencySubGeometryId,
          member_type: 'link',
          member_id: res.id,
          entry_point: 'link_editor',
        });
      }

      store.fetchData(agency!.id);
    } catch (e) {
      console.error('Error adding agency link: ', e);
      Notify.error({ message: 'Error adding agency link' });
    }
  };

  return (
    <Dialog>
      <div>
        <DialogTrigger asChild>
          <Button data-testid="agency-link-add">Add Link</Button>
        </DialogTrigger>
      </div>
      <DialogContent
        title="Add Agency Link"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="agency-link-add-dialog"
      >
        <Form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="text-sm">
            <div className="min-w-[40vw] grow space-y-2">
              <ControlledInput
                control={form.control}
                name="title"
                label="Title"
                isRequired
                tooltipText="Title for the document link"
              />

              <ControlledTextArea
                control={form.control}
                name="description"
                label="Description"
                isRequired
                tooltipText="Description for the link"
              />

              <ControlledInput control={form.control} name="url" label="Link URL" isRequired tooltipText="Link URL" />

              <ControlledCheckbox
                control={form.control}
                name="isPrivate"
                label="Private - File is not visible to the user"
              />

              <ControlledSelect
                control={form.control}
                name="type"
                label="Link Type"
                isRequired
                tooltipText="Select the type for this link"
                items={items}
              />

              <ControlledSelect
                control={form.control}
                name="agencySubGeometryId"
                label="Sub Region"
                tooltipText="Assign this link to one of the agency's sub regions, or leave unscoped"
                isRequired
                items={subRegionItems}
                data-testid="agency-sub-geometries-add-link-sub-region-select"
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="submit" data-testid="agency-link-add-submit">
              Submit
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
